import { useCallback, useMemo } from 'react';
import type { MeasurementGroup, MeasurementLabel } from '@/models/template';
import { useSettings } from '@/features/settings';
import Panel from '../shared/Panel/Panel';
import MeasurementSection from './Section/MeasurementSection';
import MeasurementRow from './Row/MeasurementRow';
import type { MeasurementRowModel } from './rowModel';
import styles from './MeasurementsPanel.module.css';

interface MeasurementsPanelProps {
  measurementGroups: MeasurementGroup[];
  /** Per-canonical resolved row state (value, marker, annotation, tool,
   *  range) — the single source shared with the click handlers.
   *  Built once via `resolveMeasurementRows` in the report orchestrator. */
  rowModels: Map<string, MeasurementRowModel>;
  /** Rows with an edit not yet saved, by canonicalMeasurement — drives the
   *  unsaved-change fill. */
  unsavedMeasurements?: Set<string>;
  onMeasurementChange: (
    canonicalMeasurement: string,
    value: number | null,
    unit: string | null
  ) => void;
  onMeasurementClick?: (measurementLabel: MeasurementLabel) => void;
  /** Reset a diverged row to its calculated value (AP-731 R17). */
  onConverge?: (canonicalMeasurement: string) => void;
  /** A row's value input gained focus — clears armed/selected (I7). */
  onMeasurementFocus?: () => void;
  /** canonicalMeasurement whose drawing tool is armed in the viewer (AP-764
   *  §3.8 "armed" — row highlight + accent label). */
  activeMeasurement?: string | null;
  /** canonicalMeasurement whose annotation is the selected trace in the viewer
   *  (AP-764 §3.10 "selected" — bold label). Resolved from selectedAnnotationUID
   *  via the annotation→measurement map. */
  selectedMeasurement?: string | null;
  disabled?: boolean;
  /** Opens a guideline PDF from a row's range tooltip (see useOpenGuideline). */
  onOpenSource?: (file: string, page?: number | null) => void;
}

export default function MeasurementsPanel({
  measurementGroups,
  rowModels,
  unsavedMeasurements,
  onMeasurementChange,
  onMeasurementClick,
  onConverge,
  onMeasurementFocus,
  activeMeasurement,
  selectedMeasurement,
  disabled = false,
  onOpenSource,
}: MeasurementsPanelProps) {
  const { settings, updateSetting } = useSettings();

  // canonicalMeasurementKey → worksheet label, for humanizing the engine's
  // missing-input keys ("TRMAX_VEL" → "TR max velocity"). Formula humanization
  // itself happens in the row model.
  const labelByKey = useMemo(() => {
    const map = new Map<string, string>();
    measurementGroups.forEach((g) =>
      g.measurementLabels.forEach((ml) => {
        if (ml.canonicalMeasurementKey)
          map.set(ml.canonicalMeasurementKey, ml.label);
      })
    );
    return map;
  }, [measurementGroups]);

  const handleChange = useCallback(
    (canonicalMeasurement: string, unit: string | null) =>
      (value: number | null) => {
        // Pass the worksheet label's unit through verbatim so a newly-created
        // manual row is stored with the backend's unit, not a fabricated one.
        onMeasurementChange(canonicalMeasurement, value, unit);
      },
    [onMeasurementChange]
  );

  const handleClick = useCallback(
    (measurementLabel: MeasurementLabel) => () => {
      onMeasurementClick?.(measurementLabel);
    },
    [onMeasurementClick]
  );

  const sortedGroups = useMemo(
    () =>
      measurementGroups
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((g) => ({
          ...g,
          measurementLabels: g.measurementLabels
            .slice()
            .sort((a, b) => a.index - b.index),
        })),
    [measurementGroups]
  );

  return (
    <Panel
      title="Measurements"
      collapsed={settings.collapsedMeasurementSection}
      onCollapsedChange={(collapsed) =>
        updateSetting({ collapsedMeasurementSection: collapsed })
      }
    >
      <div className={styles.grid}>
        {sortedGroups.map((group) => (
          <MeasurementSection key={group.uuid} title={group.label}>
            {group.measurementLabels.map((ml) => {
              const key = ml.canonicalMeasurement;
              const model = key ? rowModels.get(key) : undefined;

              // Left-gutter pencil from the Annotated state (AP-764 §3.3).
              const annotated = model?.annotated ?? null;
              let pencil: 'filled' | 'outline' | 'unavailable' | 'none' =
                'none';
              if (annotated === 'has') pencil = 'filled';
              else if (annotated === 'can') pencil = 'outline';
              else if (annotated === 'can-elsewhere') pencil = 'unavailable';

              return (
                <MeasurementRow
                  key={ml.uuid}
                  label={ml.label}
                  value={model?.value ?? null}
                  unit={model?.unit ?? ml.unit ?? ''}
                  precision={ml.precision ?? 2}
                  isClickable={
                    !!onMeasurementClick &&
                    (annotated === 'has' || (annotated === 'can' && !disabled))
                  }
                  marker={model?.marker ?? null}
                  derivedFormula={
                    model?.computed ? model.computed.formula : undefined
                  }
                  missingInputs={model?.missingOperands.map(
                    (k) => labelByKey.get(k) ?? k
                  )}
                  unsaved={!!key && !!unsavedMeasurements?.has(key)}
                  diverged={model?.divergence === 'diverged'}
                  calculatedValue={model?.calculated ?? null}
                  onConverge={
                    key && model?.divergence === 'diverged' && onConverge
                      ? () => onConverge(key)
                      : undefined
                  }
                  pencil={pencil}
                  unavailableHint={model?.unavailableHint ?? undefined}
                  range={model?.range ?? null}
                  normalRange={model?.normalRange ?? null}
                  rangeNote={model?.rangeNote ?? null}
                  source={model?.source ?? null}
                  onOpenSource={onOpenSource}
                  isActive={
                    activeMeasurement !== null && activeMeasurement === key
                  }
                  selected={
                    selectedMeasurement != null && selectedMeasurement === key
                  }
                  onChange={key ? handleChange(key, ml.unit) : undefined}
                  onClick={onMeasurementClick ? handleClick(ml) : undefined}
                  onValueFocus={onMeasurementFocus}
                  disabled={disabled}
                />
              );
            })}
          </MeasurementSection>
        ))}
      </div>
    </Panel>
  );
}
